import http from "@/http-common";

const ASSIGNMENT_URL = "/package-student/assignment";
const ADDON_URL = "/package-student/addon";
const DEFAULT_ALERT_RATIO = 0.9;
const DEFAULT_ALERT_SLOTS = 5;
const DEFAULT_ALERT_INTERVAL_DAYS = 7;
const DEFAULT_ALERT_TITLE = "Cảnh báo giới hạn học sinh";
const DEFAULT_ALERT_CONTENT = "Trường hiện có {so_hoc_sinh_hien_tai}/{gioi_han_hoc_sinh} học sinh, {trang_thai}. Vui lòng nâng cấp gói để tiếp tục thêm học sinh.";

const unwrap = (response) => response.data.data;

const normalizeStatus = (item) => {
  const hasAssignment = Boolean(item.schoolPackageId);
  const usageRatio = Number(item.usageRatio || 0);
  const packageData = item.packageId ? {
    id: Number(item.packageId),
    code: item.packageCode,
    name: item.packageName,
  } : null;

  return {
    id: Number(item.schoolId),
    school: {
      id: Number(item.schoolId),
      code: item.schoolId,
      name: item.schoolName,
    },
    assignment: hasAssignment ? {
      id: Number(item.schoolPackageId),
      schoolId: Number(item.schoolId),
      packageId: Number(item.packageId),
      assignedAt: item.assignedAt,
    } : null,
    package: packageData,
    baseLimit: Number(item.baseLimit || 0),
    addonTotal: Number(item.addonTotal || 0),
    effectiveLimit: Number(item.effectiveLimit || 0),
    currentStudents: Number(item.currentStudents || 0),
    usagePercent: usageRatio * 100,
    notifyEnabled: item.notifyEnabled !== false,
    allowExceedStudentLimit: item.allowExceedStudentLimit === true,
    autoLocked: Boolean(item.locked),
    locked: Boolean(item.locked),
    status: item.status,
    historyCount: 0,
  };
};

const normalizeAssignment = (item) => ({
  ...item,
  id: Number(item.id),
  schoolId: Number(item.schoolId),
  packageId: Number(item.packageId),
  baseLimit: Number(item.baseLimit || 0),
  addonTotal: Number(item.addonTotal || 0),
  effectiveLimit: Number(item.effectiveLimit || 0),
  currentStudents: Number(item.currentStudents || 0),
  usagePercent: Number(item.usageRatio || 0) * 100,
  allowExceedStudentLimit: item.allowExceedStudentLimit === true,
  notifyEnabled: item.notifyEnabled !== false,
  locked: Boolean(item.locked),
});

const normalizePackageHistory = (item) => {
  const baseLimit = Number(item.baseLimit || item.limitStudents || 0);
  const addonTotal = Number(item.addonTotal || 0);
  return {
    ...item,
    id: Number(item.id),
    packageId: Number(item.packageId),
    packageCode: item.packageCode || item.code || "",
    packageName: item.packageName || item.name || "",
    baseLimit,
    addonTotal,
    effectiveLimit: Number(item.effectiveLimit || baseLimit + addonTotal),
    assignedAt: item.assignedAt || item.startAt || item.createdAt || item.createdDate,
    endedAt: item.endedAt || item.endAt,
    status: item.status || (item.active ? "active" : "ended"),
  };
};

const normalizeBatchResult = (result) => ({
  ...result,
  results: (result && result.results ? result.results : []).map((item) => ({
    ...item,
    data: item.data ? normalizeAssignment(item.data) : item.data,
  })),
});

const normalizeAlertConfig = (item) => ({
  ...item,
  id: item.id ? Number(item.id) : null,
  thresholdRatio: Number(item.thresholdRatio !== undefined ? item.thresholdRatio : DEFAULT_ALERT_RATIO),
  remainingSlots: Number(item.remainingSlots !== undefined ? item.remainingSlots : DEFAULT_ALERT_SLOTS),
  notificationIntervalDays: Number(item.notificationIntervalDays !== undefined
    ? item.notificationIntervalDays : DEFAULT_ALERT_INTERVAL_DAYS),
  notificationTitle: item.notificationTitle || DEFAULT_ALERT_TITLE,
  notificationContent: item.notificationContent || DEFAULT_ALERT_CONTENT,
});

class SchoolPackageService {
  searchSchools({ schoolId, packageId, status, page = 0, size = 20 } = {}) {
    const params = { page, size };
    if (schoolId !== undefined && schoolId !== null && schoolId !== "") {
      params.schoolId = Number(schoolId);
    }
    if (packageId !== undefined && packageId !== null && packageId !== "") {
      params.packageId = Number(packageId);
    }
    if (status) params.status = status;

    return http.get(`${ASSIGNMENT_URL}/schools`, { params }).then((response) => ({
      items: (unwrap(response) || []).map(normalizeStatus),
      total: Number(response.data.total || 0),
    }));
  }

  getStatistic() {
    return http.get(`${ASSIGNMENT_URL}/statistic`)
      .then((response) => unwrap(response) || {});
  }

  assignPackage(schoolId, packageId) {
    return this.assignPackageMany([schoolId], packageId);
  }

  assignPackageMany(schoolIds, packageId) {
    return http.post(`${ASSIGNMENT_URL}/assign-batch`, {
      packageId: Number(packageId),
      schoolIds: (schoolIds || []).map((schoolIdItem) => Number(schoolIdItem)),
    }).then((response) => normalizeBatchResult(unwrap(response)));
  }

  changePackage(schoolId, packageId) {
    return http.put(`${ASSIGNMENT_URL}/change-package`, {
      schoolId: Number(schoolId),
      newPackageId: Number(packageId),
    }).then((response) => normalizeAssignment(unwrap(response)));
  }

  addAddon(schoolPackageId, amount, note) {
    return http.post(`${ADDON_URL}/add`, {
      schoolPackageId: Number(schoolPackageId),
      amount: Number(amount),
      note: String(note || "").trim(),
    }).then((response) => normalizeAssignment(unwrap(response)));
  }

  getAddonHistory(schoolPackageId) {
    return http.get(`${ADDON_URL}/history/${schoolPackageId}`).then((response) => (
      (unwrap(response) || []).map((item) => ({
        ...item,
        createdAt: item.createdDate || item.createdAt,
        amount: Number(item.amount || 0),
      }))
    ));
  }

  getPackageHistory(schoolId) {
    return http.get(`${ASSIGNMENT_URL}/history/${Number(schoolId)}`).then((response) => {
      const data = unwrap(response);
      const items = Array.isArray(data) ? data : (data && data.items) || [];
      return items.map(normalizePackageHistory);
    });
  }

  testPackageLimitAlert(schoolId) {
    return http.post(`/package-student/alert/test/${Number(schoolId)}`).then((response) => {
      const data = unwrap(response) || {};
      return {
        ...data,
        schoolId: Number(data.schoolId || schoolId),
        currentStudents: Number(data.currentStudents || 0),
        effectiveLimit: Number(data.effectiveLimit || 0),
        remaining: Number(data.remaining || 0),
        exceededStudents: Number(data.exceededStudents || 0),
        usageRatio: Number(data.usageRatio || 0),
        thresholdRatio: Number(data.thresholdRatio || 0),
        remainingSlotsConfig: Number(data.remainingSlotsConfig || 0),
        notificationIntervalDays: Number(data.notificationIntervalDays !== undefined
          ? data.notificationIntervalDays : DEFAULT_ALERT_INTERVAL_DAYS),
        receiverCount: Number(data.receiverCount || 0),
        deliveredReceiverCount: Number(data.deliveredReceiverCount || 0),
        attemptedTokenCount: Number(data.attemptedTokenCount || 0),
        successfulTokenCount: Number(data.successfulTokenCount || 0),
        failedTokenCount: Number(data.failedTokenCount || 0),
      };
    });
  }

  getPackageLimitAlertConfig() {
    return http.get("/package-student/alert/config").then((response) => {
      return normalizeAlertConfig(unwrap(response) || {});
    });
  }

  updatePackageLimitAlertConfig(config) {
    return http.put("/package-student/alert/config", {
      thresholdRatio: Number(config.thresholdRatio),
      remainingSlots: Number(config.remainingSlots),
      notificationIntervalDays: Number(config.notificationIntervalDays),
      notificationTitle: String(config.notificationTitle || "").trim(),
      notificationContent: String(config.notificationContent || "").trim(),
    }).then((response) => {
      return normalizeAlertConfig(unwrap(response) || {});
    });
  }

  getSchoolPackageAlertConfig(schoolId) {
    return http.get(`/package-student/alert/config/school/${Number(schoolId)}`).then((response) => {
      const data = unwrap(response) || {};
      return {
        ...data,
        schoolId: Number(data.schoolId || schoolId),
        notifyEnabled: data.notifyEnabled !== false,
      };
    });
  }

  updateSchoolPackageAlertConfig(schoolId, notifyEnabled) {
    return http.put(`/package-student/alert/config/school/${Number(schoolId)}`, {
      notifyEnabled: Boolean(notifyEnabled),
    }).then((response) => {
      const data = unwrap(response) || {};
      return {
        ...data,
        schoolId: Number(data.schoolId || schoolId),
        notifyEnabled: data.notifyEnabled !== false,
      };
    });
  }

  updateStudentQuotaOverride(schoolId, allowExceedStudentLimit) {
    return http.put(`${ASSIGNMENT_URL}/schools/${Number(schoolId)}/quota-override`, {
      allowExceedStudentLimit: Boolean(allowExceedStudentLimit),
    }).then((response) => {
      const data = unwrap(response) || {};
      return {
        ...data,
        schoolId: Number(data.schoolId || schoolId),
        allowExceedStudentLimit: data.allowExceedStudentLimit === true,
      };
    });
  }
}

export default new SchoolPackageService();
